import { FC, memo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { Typo } from '../../../components/Typo';
import PhotoFill from '../../../components/shared/PhotoFill';
import Button from '../../../components/shared/Button';
import FounderBadge from '../../../components/shared/FounderBadge';
import DogeIcon from '../../../components/icons/DogeIcon';
import MessageIcon from '../../../components/icons/MessageIcon';
import DistanceAway from '../../../components/shared/DistanceAway';
import { AuthUserState } from '../../../context/AuthUserProvider';
import { useHandleMessageSenderAuth } from '../../../hooks/useHandleMessageSenderAuth';
import { getListingPlaceholder } from '../../../util/listingPlaceholder';
import { LISTING_CARD_TYPES, LISTING_TYPES, ROUTES } from '../../../constants/data';
import { TRANSLATED_CONSTANTS } from '../../../static/translatedConstants';
import { formatEventDate } from './CustomPopup';
import './map.css';

// Seller-first stack card (map polish round 2): a pin holding one seller's
// multiple items used to swipe full listing cards - the seller's name repeated
// on every slide and the next card peeked in half-clipped. Since the owner
// split (ring fix), every multi-item stack IS a single owner, so this card
// leads with the seller once and lists the items as compact rows. Used by the
// desktop Leaflet popup and, with sheet={true}, by the mobile bottom sheet.
type Props = {
  listings: any[];
  sheet?: boolean;
};

const detailRoute = (listing: any) => {
  const type = listing?.type;
  return type === LISTING_CARD_TYPES.STOREFRONT
    ? `${ROUTES.STOREFRONT_DETAILS_ROUTE}/${listing?.id}`
    : type === LISTING_CARD_TYPES.STOREFRONT_ITEM
    ? `${ROUTES.STOREFRONT_ITEM_DETAILS_ROUTE}/${listing?.id}`
    : `${ROUTES.LISTING_DETAILS_ROUTE}/${listing?.id}`;
};

// Row accent dot, reusing the cluster legend colors so type reads the same
// on the pin cluster and inside the card. Only rendered for mixed stacks.
const DOT_CLASS: { [key: string]: string } = {
  personal: 'cluster-dot-personal',
  wanted: 'cluster-dot-wanted',
  storefrontItem: 'cluster-dot-storefront',
  storefront: 'cluster-dot-storefront',
  event: 'cluster-dot-event'
};

const SellerStackPopup: FC<Props> = ({ listings, sheet = false }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { authUser } = AuthUserState();
  const items = listings ?? [];
  const owner = items[0]?.user;
  const shared = items[0]?.location;
  const mixedTypes = new Set(items.map((item: any) => item?.type)).size > 1;
  // One message button for the whole stack (same owner). If every entry is a
  // wanted/buying post the owner is a buyer; any selling item makes the
  // seller wording right.
  const allWanted = items.every(
    (item: any) => item?.type === LISTING_CARD_TYPES.WANTED || item?.type === LISTING_TYPES.BUYING
  );
  const { onSendMessage } = useHandleMessageSenderAuth(items[0]);

  return (
    <div
      className={clsx(
        'rounded-xl bg-white font-inter',
        sheet ? 'w-full' : 'w-[240px] sm:w-[288px]'
      )}>
      <div className={clsx('pt-3 pb-2', sheet ? 'px-4' : 'px-3')}>
        <div className="flex items-center gap-2">
          <Typo.textMd fontWeight="font-semibold" className="truncate text-gray-900">
            {owner?.name || owner?.username}
          </Typo.textMd>
          <FounderBadge userId={owner?.id} size="sm" />
        </div>
        <Typo.textXs fontWeight="font-normal" className="mt-0.5 text-gray-500">
          <FormattedMessage
            id="map.seller_stack.items_here"
            defaultMessage="{count, plural, one {# item at this spot} other {# items at this spot}}"
            values={{ count: items.length }}
          />
        </Typo.textXs>
      </div>
      <div
        className={clsx(
          'divide-y divide-gray-100 border-t border-gray-100',
          !sheet && 'max-h-[236px] overflow-y-auto'
        )}>
        {items.map((item: any) => {
          const type = item?.type;
          const image = item?.image?.length > 0 ? item.image[0].url : getListingPlaceholder(type);
          return (
            <button
              key={`${type}-${item?.id}`}
              type="button"
              onClick={() => navigate(detailRoute(item))}
              className={clsx(
                'flex w-full items-center gap-3 text-start hover:bg-gray-50 focus:outline-none focus-visible:bg-gray-50',
                sheet ? 'px-4 py-2.5' : 'px-3 py-2'
              )}>
              <PhotoFill
                className={clsx('shrink-0 rounded-lg', sheet ? 'h-14 w-14' : 'h-11 w-11')}
                src={image}
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  {mixedTypes && (
                    <span
                      className={clsx(
                        'cluster-dot shrink-0',
                        DOT_CLASS[type] ?? 'cluster-dot-storefront'
                      )}
                    />
                  )}
                  <Typo.textXs fontWeight="font-medium" className="truncate text-gray-900">
                    {item?.name}
                  </Typo.textXs>
                </div>
                <div className="mt-0.5">
                  {type === LISTING_TYPES.EVENT ? (
                    <Typo.textXs fontWeight="font-normal" className="text-gray-600">
                      {formatEventDate(item?.eventStartDate)}
                    </Typo.textXs>
                  ) : type === LISTING_CARD_TYPES.STOREFRONT ? (
                    <Typo.textXs fontWeight="font-normal" className="text-gray-600">
                      {intl.formatMessage({
                        id: TRANSLATED_CONSTANTS.STOREFRONT.id,
                        defaultMessage: TRANSLATED_CONSTANTS.STOREFRONT.defaultMessage
                      })}
                    </Typo.textXs>
                  ) : (
                    <span className="flex items-center gap-1.5">
                      <Typo.textXs
                        fontWeight="font-semibold"
                        className="flex items-center gap-0.5 text-gray-900">
                        <DogeIcon className="h-3.5 w-3.5" />
                        {Math.round(item?.price?.priceInDoge)} DOGE
                      </Typo.textXs>
                      <Typo.textXs fontWeight="font-normal" className="text-gray-500">
                        ${Number(item?.price?.priceInUSD)?.toFixed(2)}
                      </Typo.textXs>
                    </span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
      <div
        className={clsx(
          'flex items-center justify-between border-t border-gray-100 py-2',
          sheet ? 'px-4' : 'px-3'
        )}>
        <Button
          type="button"
          theme="blue-link"
          disabled={authUser?.id == owner?.id}
          onClick={(event: any) => {
            onSendMessage();
            event.stopPropagation();
          }}
          icon={<MessageIcon className="text-blue-gray-600"></MessageIcon>}>
          <Typo.textSm fontWeight="font-semibold" className="text-blue-gray-600 underline">
            {allWanted
              ? intl.formatMessage({
                  id: 'shared.message-buyer',
                  defaultMessage: 'Message Buyer'
                })
              : intl.formatMessage({
                  id: 'shared.message-seller',
                  defaultMessage: 'Message Seller'
                })}
          </Typo.textSm>
        </Button>
        <DistanceAway
          latitude={shared?.latitude}
          longitude={shared?.longitude}
          className="text-xs text-gray-500"
        />
      </div>
    </div>
  );
};

// Memoized (map plan A4) - popup content only re-renders when its stack
// changes, not on every MapboxLe render during pans.
export default memo(SellerStackPopup);
